import { useState } from 'react';
import { X } from 'lucide-react';
import type { EventDetail } from '../../api/types';

// 권위: docs/ARCH_DASHBOARD.md §4.8 "우측 상세 패널".
// 본 구현은 모달 오버레이 + /logs/:eventId 딥링크(컨테이너에서 처리).

interface Props {
  detail: EventDetail | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  onClose: () => void;
  onAck: (note: string) => void;
  ackBusy: boolean;
}

export function SystemLogDetail({
  detail,
  isLoading,
  isError,
  error,
  onClose,
  onAck,
  ackBusy,
}: Props): JSX.Element {
  const [note, setNote] = useState('');
  const acked = (detail?.acks?.length ?? 0) > 0;

  return (
    <div
      className="syslog-modal__backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="syslog-modal" role="dialog" aria-label="시스템 이벤트 상세">
        <header className="syslog-modal__header">
          {detail ? (
            <div className="syslog-modal__title-group">
              <span className={`syslog-badge syslog-badge--${detail.severity.toLowerCase()}`}>
                {detail.severity}
              </span>
              <code className="syslog-modal__code">{detail.eventCode}</code>
              <span className="syslog-modal__meta">
                {formatTs(detail.occurredAt)} · {detail.sourceAgent}
              </span>
            </div>
          ) : (
            <div className="syslog-modal__title-group">
              <span>상세</span>
            </div>
          )}
          <button
            type="button"
            className="syslog-modal__close"
            onClick={onClose}
            aria-label="닫기"
          >
            <X size={18} strokeWidth={1.75} />
          </button>
        </header>

        <div className="syslog-modal__body">
          {isLoading && <div className="syslog-empty">불러오는 중…</div>}
          {isError && (
            <div className="syslog-warning-banner">
              상세 조회 실패: {error?.message ?? 'unknown'}
            </div>
          )}

          {detail && (
            <>
              <section className="syslog-section">
                <h4 className="syslog-section__title">요약</h4>
                <div>{detail.summary || <span className="syslog-muted">(없음)</span>}</div>
              </section>

              <section className="syslog-section">
                <h4 className="syslog-section__title">Payload</h4>
                {detail.payload ? (
                  <JsonBlock value={detail.payload} />
                ) : (
                  <div className="syslog-muted">(payload 없음)</div>
                )}
                {detail.payloadRef && (
                  <div className="syslog-muted">payload_ref: {detail.payloadRef}</div>
                )}
              </section>

              {detail.entityRefs && Object.keys(detail.entityRefs).length > 0 && (
                <section className="syslog-section">
                  <h4 className="syslog-section__title">Entity refs</h4>
                  <JsonBlock value={detail.entityRefs} />
                </section>
              )}

              <section className="syslog-section">
                <h4 className="syslog-section__title">Meta</h4>
                <dl className="syslog-meta">
                  <dt>trace_id</dt>
                  <dd>{detail.traceId ?? <span className="syslog-muted">-</span>}</dd>
                  <dt>tags</dt>
                  <dd>
                    {detail.tags.length > 0 ? (
                      detail.tags.map((t) => (
                        <span key={t} className="syslog-tag">
                          {t}
                        </span>
                      ))
                    ) : (
                      <span className="syslog-muted">-</span>
                    )}
                  </dd>
                  <dt>schema_version</dt>
                  <dd>{detail.schemaVersion ?? <span className="syslog-muted">-</span>}</dd>
                </dl>
              </section>

              <section className="syslog-section">
                <h4 className="syslog-section__title">읽음 처리 이력 ({detail.acks.length})</h4>
                {detail.acks.length === 0 ? (
                  <div className="syslog-muted">아직 읽음 처리되지 않았습니다.</div>
                ) : (
                  <ul className="syslog-acks">
                    {detail.acks.map((a, i) => (
                      <li key={`${a.ackBy}-${i}`}>
                        <b>{a.ackBy}</b> · {formatTs(a.ackAt)}
                        {a.note ? <span className="syslog-muted"> — {a.note}</span> : null}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </>
          )}
        </div>

        <footer className="syslog-modal__actions">
          <input
            className="syslog-input syslog-input--note"
            type="text"
            placeholder="(선택) 읽음 처리 메모"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={acked || ackBusy || !detail}
          />
          <button
            type="button"
            className="syslog-btn syslog-btn--primary"
            disabled={acked || ackBusy || !detail}
            onClick={() => {
              onAck(note.trim() || '');
              setNote('');
            }}
          >
            {acked ? '이미 읽음 처리됨' : ackBusy ? '처리 중…' : '읽음 처리'}
          </button>
          <button type="button" className="syslog-btn syslog-btn--ghost" onClick={onClose}>
            닫기
          </button>
        </footer>
      </div>
    </div>
  );
}

function JsonBlock({ value }: { value: unknown }): JSX.Element {
  let json: string;
  try {
    json = JSON.stringify(value, null, 2);
  } catch {
    json = String(value);
  }
  return <pre className="syslog-json">{highlightJson(json)}</pre>;
}

// 아주 가벼운 JSON 하이라이트 — key/string/number/boolean 에 span 클래스 부여.
function highlightJson(json: string): JSX.Element[] {
  const tokenRe =
    /("(?:\\.|[^"\\])*")\s*:|("(?:\\.|[^"\\])*")|\b(true|false|null)\b|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g;
  const out: JSX.Element[] = [];
  let lastIndex = 0;
  let idx = 0;
  let m: RegExpExecArray | null;
  while ((m = tokenRe.exec(json)) !== null) {
    if (m.index > lastIndex) {
      out.push(<span key={`t-${idx++}`}>{json.slice(lastIndex, m.index)}</span>);
    }
    if (m[1] !== undefined) {
      out.push(
        <span key={`t-${idx++}`} className="syslog-json__key">
          {m[1]}
        </span>,
        <span key={`t-${idx++}`}>:</span>
      );
    } else if (m[2] !== undefined) {
      out.push(
        <span key={`t-${idx++}`} className="syslog-json__str">
          {m[2]}
        </span>
      );
    } else if (m[3] !== undefined) {
      out.push(
        <span key={`t-${idx++}`} className="syslog-json__bool">
          {m[3]}
        </span>
      );
    } else if (m[4] !== undefined) {
      out.push(
        <span key={`t-${idx++}`} className="syslog-json__num">
          {m[4]}
        </span>
      );
    }
    lastIndex = tokenRe.lastIndex;
  }
  if (lastIndex < json.length) {
    out.push(<span key={`t-${idx++}`}>{json.slice(lastIndex)}</span>);
  }
  return out;
}

function formatTs(iso: string): string {
  try {
    return new Date(iso).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    return iso;
  }
}
